<template>
    <div class="test animated" :key="currentTestIndex" :class="switchingTestClass">
        <div :class="{'loading' : testAdded}">
            <img class="animatedLogo" v-bind:src="logo" v-if="testAdded"/>
            <div class="loading-text" v-if="testAdded">Adding stars</div>
        </div>
        <div class="confirm animated" v-show="confirmContinue">
            You currently have stars assigned which has not been saved for the current filters.
            <div class="actionButtons">
                <div class="btn" @click="confirmContinue = false">Cancel</div><div @click="skipToTest(direction)" class="btn discard">Discard and continue</div>
            </div>
        </div>
        <div class="title" :class="hideRange">{{test.title}}</div>
        <div class="description" :class="hideRange">{{test.description}}</div>
        <div class="legend" v-if="test.legend !== undefined">
            <div class="legendKey" v-for="(legendItem, key) in test.legend" :key="key">
                <div class="circle" :style="{'background': legendItem.color}"></div>
                {{legendItem.title}}
            </div>
        </div>
        <div v-for="filter in test.filters" :key="filter.title">
            <div class="dropdown-wrapper" v-if="filter.type === 'Category' && (filter.title !== 'Complexity' && filter.title !== 'LCP rating') && filter.visible">
                <dropdownInput
                    :ref="filter.title"
                    :mutationRef="$store.state.universe.data.categoryDimensions[2].name"
                    :title="$store.state.universe.data.categoryDimensions[2].name"
                    :options="$store.state.universe.data.categoryDimensions[2].order"
                    :index="1.8"
                    :key="$store.state.universe.data.categoryDimensions[2].name"
                    :multi="true"
                    mutationAction=""
                    mutationOption=""
                    overflowy
                    class="test"/>
            </div>
			<starsTable
				@rating="storeRating"
				@currentRating="saveTestTable"
				v-if="filter.type === 'Category' && (filter.title === 'Complexity' || filter.title === 'LCP rating') && filter.visible"
				:types="starTableTypes(filter.title)"/>
            <div slot="header"
                @show="test()"
                class="head-content"></div>
            <range-slider
                v-if="filter.type !== 'Category' && filter.visible"
                :ref="filter.title"
                :index="1"
                :notAbsolute="true"
                :min="currentTestSet(filter.title).min"
                :value="[
					Math.max(currentTestSet(filter.title).min, currentTestSet(filter.title).values[0]),
					Math.min(currentTestSet(filter.title).max, currentTestSet(filter.title).values[1])
				]"
                :max="currentTestSet(filter.title).max"
                :interval="currentTestSet(filter.title).sliderInterval"
                :prefix="currentTestSet(filter.title).prefix"
                :suffix="currentTestSet(filter.title).suffix"
                :roundTo="currentTestSet(filter.title).roundTo"
                :valueName="currentTestSet(filter.title).valueName"
                v-on:input="sliderFilter($event, currentTestSet(filter.title).valueName)"
                :id="`${currentTestIndex}-${filter.title}`"/>
        </div>
        <ratingInputTests
			title="Stars"
			@rating="storeRating"
			@currentRating="saveTest"
			:class="hideRange"
			v-if="$store.state.chart.type !== 'column'"
			/>
        <div class="actions" :class="hideRange">
            <div v-if="showBack" @click="nextTest('prev')" class="btn">Prev test</div>
            <div v-if="showNext" @click="nextTest('next')" class="btn next">Next test</div>
            <div v-if="showFinish" @click="finishTests" class="btn next finish" :class="{'disabled': !this.minTestsCompleted}" v-tooltip="finishText">View results</div>
        </div>
        <div v-show="testResults[test.title].rating > 0" class="currentTests" :class="{'open' : showHistory, 'animated bounce' : testAdded}">
            <div class="title" @click="showHistory = !showHistory">{{testResults[test.title].filters.length}} {{test.title}} test(s)</div>
            <div class="filtersWrapper">
                <div class="filter" v-for="(filter, key) in testResults[test.title].filters" :key="key">
                    <div class="delete" @click="deleteTest(key)"><i class="icon-trash-2"></i></div>
                    <strong>Stars: {{filter.rating}}</strong> ({{filter.funds.length}} funds)
                    <div class="filters" v-for="(filters, key) in filter.filter.options" :key="key">
                        <strong>{{filters.metric}}</strong>
                        <span v-for="(values, key) in filters.properties" :key="key">
                            {{filterDisplay(values, filters.metric)}}
                            <span v-if="filters.metricSuffix !== null && filters.metric !== 'AUM' && filters.metric !=='Length of track record'">
								{{filters.metricSuffix}}
							</span>
                            <span v-if="key < (filters.properties.length-1 && filters.type === 'Value')">to </span>
                            <span v-if="key < (filters.properties.length-1 && filters.type === 'Category')">, </span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import logo from '@/assets/svg/select_animated_new.svg';

export default {
    name: 'tests',
    props: ['config'],
    data () {
        return {
            confirmContinue: false,
            currentTestIndex: 0,
            direction: 'next',
            logo,
            minTestsCompleted: false,
            testAdded: false,
            testResults: {},
            testSwitched: false,
            showHistory: false,
            complexity: [
                'Simple',
                'Medium',
                'Complex',
                'Unrated',
            ],
        };
    },
    created () {
        this.rating = 0;
        this.setTest();
    },
    computed: {
        showBack () {
            return this.currentTestIndex > 0 && this.currentTestIndex < this.config.length;
        },
        showNext () {
            return this.currentTestIndex < this.config.length - 1;
        },
        showFinish () {
            return (this.currentTestIndex === this.config.length - 1);
        },
        test () {
            return this.config[this.currentTestIndex];
        },
        hideRange () {
            return !this.showList && this.$store.state.ui.dropdownSelected ? 'hideRange' : '';
        },
        finishText () {
            return this.minTestsCompleted ? '' : 'Please complete at least 1 tests to continue';
        },
        switchingTestClass () {
            return this.testSwitched ? 'fadeInRight' : '';
        },
        showConfirm () {
            return this.confirmContinue ? 'fadeIn' : '';
        },
        visiblePoints () {
            return this.$store.state.chart.visiblePoints;
        },
    },
    methods: {
        starTableTypes (title) {
            return title === 'Complexity' ? this.complexity : this.$store.state.universe.data.categoryFilter[2].orderNames;
        },
        deleteTest (index) {
            this.testResults[this.test.title].filters.splice(index, 1);
        },
        // updates the slider values when sliders are moved
        sliderFilter (newValue, sliderName) {
            const change = {
                name: sliderName,
                newValue,
            };
            this.$emit('updateValueFilterElement', change);
            this.$store.commit('updateValueFilterElement', change);
        },
        skipToTest (dir) {
            this.rating = 0;
            this.confirmContinue = false;
            this.$store.commit('resetValueFilter');
            // this.$store.commit('resetCategoryFilter');
            (dir === 'next' ? this.currentTestIndex += 1 : this.currentTestIndex--);
            this.setTest();
        },
        nextTest (dir) {
            this.removeCurrentCategory();
            if (this.rating !== 0) {
                this.confirmContinue = true;
                this.direction = dir;
            } else {
                this.$store.commit('resetValueFilter');
                this.$store.commit('resetCategoryFilter');
                (dir === 'next' ? this.currentTestIndex += 1 : this.currentTestIndex--);
                this.setTest();
            }
        },
        finishTests () {
            this.minTestsCompleted ? this.$store.commit('showTestTable', this.testResults) : '';
        },
        setTest () {
            const xAxis = this.test.filters.findIndex(x => x.axis === 'x');
            const yAxis = this.test.filters.findIndex(x => x.axis === 'y');
            this.$store.commit('chart/updateXaxisSelection', this.test.filters[xAxis].title);
            this.$store.commit('chart/updateYaxisSelection', this.test.filters[yAxis].title);
            this.$store.commit('chart/updateChartType', this.test.type);
            this.removeCurrentCategory();
            if (this.test.title === 'Experience') {
                // update graph to use length of experience for colours
                this.$store.commit('chart/updateColourSelection', 'Asset class');
                this.$store.commit('chart/updateRaxisSelection', 'Length of track record(Years)');
            } else if (this.test.title === 'Research view') {
                this.$store.commit('chart/updateColourSelection', 'LCP rating');
                // update graph to use colours based on LCP Rating (Buy, Hold, Review, Unrated)
                this.$store.commit('chart/updateRaxisSelection', 'AUM');
            } else {
                this.$store.commit('chart/updateColourSelection', 'Asset class');
                this.$store.commit('chart/updateRaxisSelection', 'AUM');
            }

            if (this.testResults[this.test.title] === undefined) {
                const newTest = { rating: 0, filters: [], categories: [] };
                this.$set(this.testResults, this.test.title, newTest);
            }
            this.testSwitched = true;
            setTimeout(() => { this.testSwitched = false; }, 2000);
            this.showHistory = false;
        },
        getVisibleForTest () {
            const funds = this.visiblePoints.map(fund => fund.info.id);
            return funds;
        },
        saveTestTable (rating) {
            let getFunds = [];
            const filter = [];
            let points = [];
            let objVal;
            const Simple = 0;
            const Medium = 0;
            const Complex = 0;
            const Unrated = 0;

            for (let i = 0; i < this.test.filters.length; i += 1) {
                if (this.test.filters[i].visible) {
                    for (let r = 0; r < rating.length; r += 1) {
                        if (this.test.filters[i].title === 'Complexity') {
                            getFunds = [];
                            for (let point = 0; point < this.visiblePoints.length; point += 1) {
                                const fundIndex = this.$store.state.universe.data.allMetaData.findIndex(x => x.Guid === this.visiblePoints[point].info.id);
                                if (fundIndex > -1) {
                                    if (this.$store.state.universe.data.allMetaData[fundIndex].Complexity === rating[r].type) {
                                        getFunds.push(this.visiblePoints[point].info.id);
                                    }
                                }
                            }
                        } else {
                            points = this.visiblePoints.filter(i => i.info['LCP rating'] === rating[r].type);
                            getFunds = points.map(p => p.id);
                        }
                        if (rating[r].starRating !== 0) {
                            this.testResults[this.test.title].rating = this.testResults[this.test.title].rating + rating[r].starRating;
                            const config = {
                                metric: this.test.filters[i].title,
                                metricSuffix: this.test.filters[i].suffix,
                                axis: this.test.filters[i].axis,
                                type: this.test.filters[i].type,
                                properties: rating[r].type,
                            };
                            filter.push(config);
                            const currentTest = {
                                rating: rating[r].starRating,
                                filter: {
                                    metric: this.test.title,
                                    metricSuffix: '%',
                                    options: [{
                                        metric: this.test.filters[i].title,
                                        metricSuffix: this.test.filters[i].suffix,
                                        axis: this.test.filters[i].axis,
                                        type: this.test.filters[i].type,
                                        properties: [rating[r].type],
                                    }],
                                },
                                category: {
                                    metric: this.test.title,
                                    metricSuffix: '%',
                                    options: [{
                                        metric: this.test.filters[i].title,
                                        metricSuffix: this.test.filters[i].suffix,
                                        axis: this.test.filters[i].axis,
                                        type: this.test.filters[i].type,
                                        properties: [rating[r].type],
                                    }],
                                },
                                dateRange: {
                                    minDate: this.$store.state.ui.minimumDate,
                                    maxDate: this.$store.state.ui.maximumDate,
                                },
                                funds: getFunds,
                            };
                            this.testResults[this.test.title].filters.push(currentTest);
                        }
                    }
                }
            }

            this.testAdded = true;
            this.minTestsCompleted = true;
            this.rating = 0;
            setTimeout(() => { this.testAdded = false; }, 2000);
        },
        saveTest (newRating) {
            this.testResults[this.test.title].rating = this.testResults[this.test.title].rating + newRating;
            const getFunds = this.getVisibleForTest();
            const filter = [];
            let objVal;
            for (let i = 0; i < this.test.filters.length; i += 1) {
                if (this.test.filters[i].visible) {
                    if (this.test.filters[i].type === 'Value') {
                        objVal = this.$refs[this.test.filters[i].title][0].$refs.slider.value.slice();
                    } else if (this.$refs[this.test.filters[i].title][0].chosenOptions !== undefined) {
                        objVal = this.$refs[this.test.filters[i].title][0].chosenOptions.slice();
                    }
                    const config = {
                        metric: this.test.filters[i].title,
                        metricSuffix: this.test.filters[i].suffix,
                        axis: this.test.filters[i].axis,
                        type: this.test.filters[i].type,
                        properties: objVal,
                    };
                    filter.push(config);
                }
            }

            const currentTest = {
                rating: newRating,
                filter: {
                    metric: this.test.title,
                    metricSuffix: '%',
                    options: filter,
                },
                category: {
                    metric: this.test.title,
                    metricSuffix: '%',
                    options: filter,
                },
                dateRange: {
                    minDate: this.$store.state.ui.minimumDate,
                    maxDate: this.$store.state.ui.maximumDate,
                },
                funds: getFunds,
            };
            this.testResults[this.test.title].filters.push(currentTest);
            this.testAdded = true;
            this.minTestsCompleted = true;
            this.rating = 0;
            this.resetFilterSliders();
            setTimeout(() => { this.testAdded = false; }, 2000);
        },
        currentTestSet (title) {
            const index = this.$store.state.universe.data.valueFilter.findIndex(x => x.valueName === title);
            return this.$store.state.universe.data.valueFilter[index];
        },
        filterDisplay (value, metric) {
            if (metric === 'Length of track record') {
                return `${String((value).toFixed(1))} years`;
            } if (metric === 'Standard deviation (% pa)' || metric === 'Return (% - pa over 1y)') {
                return `${Math.round(value * 10) / 10}`;
            } if (metric === 'AUM') {
                return `£${value / 1000}bn`;
            }
            return value;
        },
        storeRating (rating) {
            this.rating = rating;
        },
        removeCurrentCategory () {
            const category = this.test.filters.findIndex(x => x.type === 'Category');
            const result = category > -1 ? this.$store.state.categoryFilter.findIndex(z => z.categoryName === this.test.filters[category].title) : -1;
            if (result > -1) {
                const newCategoryFilter = this.$store.state.categoryFilter.slice(0);
                newCategoryFilter.splice(result, 1);
                this.$store.commit('updateCategoryFilter', newCategoryFilter);
            }
        },
        resetFilterSliders () {
            function round (number, dp) {
                return Math.round(number * (10 ** dp)) / (10 ** dp);
            }
            this.test.filters.forEach(filter => {
                this.sliderFilter(
                    [
                        round(this.currentTestSet(filter.title).min, this.currentTestSet(filter.title).roundTo),
                        round(this.currentTestSet(filter.title).max, this.currentTestSet(filter.title).roundTo)
                    ],
                    this.currentTestSet(filter.title).valueName
                )
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/base.scss';
.test {
    .dropdown-wrapper {
        clear: both;
        height: 80px;
        width: 100%;
        .test {
            top: 96px!important;
        }
    }
    @media (max-width: 1536px) {
        .dropdown-wrapper {
            height: 70px;
            .test {
                top: 110px!important;
            }
        }
    }
    .confirm {
        background: rgba(255,255,255,1);
        bottom: 0;
        left: 0;
        position: absolute;
        right: 0;
        top: 0;
        z-index: 6;
        .actionButtons{
            margin-top:20px;
            .btn{
                &.discard{
                    float: right;
                }
            }
        }
    }
    .loading {
        background-color: #fff;
        height: 100vh;
        position: absolute;
        width: 100%;
        z-index: 98;
        .animatedLogo {
            display: block;
            margin-left: auto;
            margin-right: auto;
            margin-top: 150px;
            top:30%;
            width: 50%;
            z-index: 99;
        }
        .loading-text {
            text-align: center;
        }
    }
    .title {
        color: $LCPMarine;
        font-size: 18px;
        font-weight: 700;
    }
    .description {
        padding: 10px 0;
    }
    .legend {
        margin-bottom: 10px;
        .legendKey {
            display: inline-block;
            margin-right: 15px;
            vertical-align: middle;
            .circle {
                border-radius: 50%;
                display: inline-block;
                height: 16px;
                vertical-align: middle;
                width: 16px;
            }
        }
    }
    .currentTests {
        background: $bodyBg;
        bottom: 0;
        height: 38px;
        left: 0;
        position: absolute;
        right: 0;
        transition: height .5s linear;
        z-index: 99;
        &.open {
            height: 330px;
        }
        .filtersWrapper {
            max-height: 300px;
            overflow-y: auto;
        }
        .filter {
            padding:10px;
            position: relative;
            &:nth-child(even) {
                background: #fefefe;
            }
            .delete {
                color: grey;
                cursor: pointer;
                font-size: 18px;
                position: absolute;
                right: 15px;
            }
        }
        .title {
            background-color: $LCPWatermelon;
            border-radius: 6px 6px 0 0;
            color: white;
            cursor: pointer;
            padding: 10px;
        }
    }
    .next {
        float: right;
    }
    .finish {
        background-color: $LCPWatermelon;
        &.disabled {
            background-color: lightgray;
            cursor: default;
        }
    }
    .hideRange {
        display: none;
    }
}
@media (max-width: 1536px) {
    .currentTests {
        bottom: -10px;
        height: 40px;
    }
    .title {
        font-size: 16px;
    }
}
</style>
